import { create } from 'zustand';
import { TOKEN_KEY } from '@/api/client';
import { getMe, type Me } from '@/api/me';

/**
 * 会话状态。
 *
 * 🔴 token 的唯一存放位置是 localStorage[TOKEN_KEY]；写入只经 signIn，清除只经 signOut
 * 或 api/client.ts 的 401 拦截器。组件里不要直接碰 localStorage。
 *
 * `status` 三态是关键：应用启动时 token 可能存在但已过期，必须先打一发 /me/get 才知道
 * 会话是否还活着。没有 'loading' 态的话，RequireAuth 会在校验完成前就把人踢去登录页。
 */
type Status = 'loading' | 'authenticated' | 'anonymous';

interface SessionState {
  status: Status;
  me: Me | null;
  /** 应用启动时调用一次：有 token 就去换 me，没有就直接置 anonymous。 */
  bootstrap: () => Promise<void>;
  signIn: (token: string, me: Me) => void;
  signOut: () => void;
  /**
   * 就地更新当前身份，避免整页重新拉取。
   * 目前唯一的调用点：管理员在成员页改了**自己**的姓名 —— 顶栏和概览页都从这里读姓名。
   */
  setMe: (me: Me) => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  status: 'loading',
  me: null,

  bootstrap: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      set({ status: 'anonymous', me: null });
      return;
    }
    try {
      const me = await getMe();
      set({ status: 'authenticated', me });
    } catch {
      // 401 的清理由 client.ts 的拦截器做；这里只负责把状态落下来。
      // 其它错误（比如后端挂了）也当未登录处理 —— 让人卡在 loading 转圈更糟。
      set({ status: 'anonymous', me: null });
    }
  },

  signIn: (token, me) => {
    localStorage.setItem(TOKEN_KEY, token);
    set({ status: 'authenticated', me });
  },

  signOut: () => {
    localStorage.removeItem(TOKEN_KEY);
    set({ status: 'anonymous', me: null });
  },

  setMe: (me) => set({ me }),
}));

/**
 * 便捷判定。角色的真相源始终是服务端每次请求重新查出来的，这里只是 UI 显示用。
 * 权限取并集：只要**带** admin 就算管理员，同时带 finance / member 不影响。
 */
export function useIsAdmin(): boolean {
  return useSessionStore((s) => s.me?.roles.includes('admin') ?? false);
}
